import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, Building2, Trees, Sofa, Bed, Monitor, Warehouse, Boxes, HeartHandshake, Leaf, Hammer, Mountain, Wrench, KeyRound, Briefcase, Recycle, Music2, AlertOctagon, Tent, Truck, ArrowUpRight, Phone } from 'lucide-react';
type ServiceItem = {
  name: string;
  desc: string;
  icon: React.ReactNode;
};
type Category = {
  id: string;
  number: string;
  title: string;
  tagline: string;
  icon: React.ReactNode;
  items: ServiceItem[];
};
const iconClass = 'w-5 h-5';
const serviceCategories: Category[] = [{
  id: 'residential',
  number: '01',
  title: 'Household & Residential',
  tagline: 'From a single mattress to the whole estate.',
  icon: <Home className="w-7 h-7" data-id="element-496" data-mp="q2n5q75w26of" />,
  items: [{
    name: 'Furniture & Appliances',
    desc: 'Couches, beds, refrigerators, washers, dryers, water heaters',
    icon: <Sofa className={iconClass} data-id="element-497" data-mp="g7262foalihu" />
  }, {
    name: 'Mattresses',
    desc: 'Any size, any quantity',
    icon: <Bed className={iconClass} data-id="element-498" data-mp="3b2xwhpchizkg" />
  }, {
    name: 'E-Waste',
    desc: 'TVs, computers, electronics',
    icon: <Monitor className={iconClass} data-id="element-499" data-mp="2xcuh9ic0w64i" />
  }, {
    name: 'Garage Cleanouts',
    desc: 'Complete floor-to-ceiling removal',
    icon: <Warehouse className={iconClass} data-id="element-500" data-mp="328i59uerqt8f" />
  }, {
    name: 'Attic & Basement',
    desc: "Even the stuff that's been up there for 20 years",
    icon: <Boxes className={iconClass} data-id="element-501" data-mp="3u1ik37f63mtc" />
  }, {
    name: 'Hoarder Cleanouts',
    desc: 'Non-judgmental, thorough, compassionate',
    icon: <HeartHandshake className={iconClass} data-id="element-502" data-mp="3l13ye6yak5pb" />
  }, {
    name: 'Backyard Debris',
    desc: 'Green waste, junk piles, old equipment, fencing',
    icon: <Leaf className={iconClass} data-id="element-503" data-mp="24p6x24enzhie" />
  }]
}, {
  id: 'construction',
  number: '02',
  title: 'Construction & Commercial',
  tagline: 'Job site cleared. No coordination required.',
  icon: <Building2 className="w-7 h-7" data-id="element-504" data-mp="1plx61p9delhl" />,
  items: [{
    name: 'Construction Debris',
    desc: 'Wood, drywall, carpet, insulation, and job site waste',
    icon: <Hammer className={iconClass} data-id="element-505" data-mp="1gpy0i99k6e6f" />
  }, {
    name: 'Concrete & Dirt',
    desc: 'Slabs, chunks, fill dirt — hauled away completely',
    icon: <Mountain className={iconClass} data-id="element-506" data-mp="16unw5evotuph" />
  }, {
    name: 'Demolition Services',
    desc: 'Sheds, fences, small structures broken down and removed',
    icon: <Wrench className={iconClass} data-id="element-507" data-mp="xsmy4fsmr4mr" />
  }, {
    name: 'Property Cleanouts',
    desc: 'Foreclosures, evictions, estate sales',
    icon: <KeyRound className={iconClass} data-id="element-508" data-mp="svo7w8c37htu" />
  }, {
    name: 'Commercial Spaces',
    desc: 'Office spaces, warehouses, retail clearing',
    icon: <Briefcase className={iconClass} data-id="element-509" data-mp="2n7l4sgtatfwa" />
  }]
}, {
  id: 'specialty',
  number: '03',
  title: 'Specialty & Exterior',
  tagline: 'The heavy stuff nobody else wants to touch.',
  icon: <Trees className="w-7 h-7" data-id="element-510" data-mp="2zpf1x9zve5sw" />,
  items: [{
    name: 'Yard & Green Waste',
    desc: 'Trimmings, branches, cardboard, organic debris',
    icon: <Recycle className={iconClass} data-id="element-511" data-mp="39g9z39enmpt4" />
  }, {
    name: 'Specialty Items',
    desc: 'Hot tubs, Jacuzzis, pianos, pool tables — the heavy stuff',
    icon: <Music2 className={iconClass} data-id="element-512" data-mp="2bum50v5o8jkr" />
  }, {
    name: 'Illegal Dumping',
    desc: 'Public spaces, private property, commercial areas',
    icon: <AlertOctagon className={iconClass} data-id="element-513" data-mp="2lpmd5k7wlznk" />
  }, {
    name: 'Encampment Cleanups',
    desc: 'Handled professionally, safely, and completely',
    icon: <Tent className={iconClass} data-id="element-514" data-mp="10q9ckd6zpit" />
  }, {
    name: 'Moving & Deliveries',
    desc: 'Need items moved or delivered? We handle that too.',
    icon: <Truck className={iconClass} data-id="element-515" data-mp="544shjwfgm0h" />
  }]
}];
export function Services() {
  const [activeTab, setActiveTab] = useState<string>(serviceCategories[0].id);
  const active = serviceCategories.find(c => c.id === activeTab) || serviceCategories[0];
  return <section id="services" className="relative py-24 lg:py-32 bg-[#0E0E0E] text-white overflow-hidden" data-id="element-516" data-mp="dju461mpfllj">
      {/* Subtle radial gradient backdrop */}
      <div className="absolute inset-0 opacity-60 pointer-events-none" data-id="element-517" data-mp="mj8t2wn83o3x">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#C8102E] rounded-full blur-[180px] opacity-[0.12]" data-id="element-518" data-mp="hknpcn8rimaq" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#C8102E] rounded-full blur-[180px] opacity-[0.08]" data-id="element-519" data-mp="wa3g438amrqf" />
      </div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
      backgroundSize: '60px 60px'
    }} data-id="element-520" data-mp="15a89vakkdrm4" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-521" data-mp="1owa6cpzeis8t">
        {/* Header */}
        <div className="max-w-3xl mb-16" data-id="element-522" data-mp="1ax22vkw7gxyt">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-6" data-id="element-523" data-mp="1avzepkq9p9ml">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C8102E] animate-pulse" data-id="element-524" data-mp="s3og432nbxua" />
            <span className="text-[11px] tracking-[0.25em] font-bold uppercase text-gray-300" data-id="element-525" data-mp="w7me2ag8j2lr">
              Full-Service Removal Menu
            </span>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-6xl font-bold leading-[1.05] mb-6" data-id="element-526" data-mp="n5my7lgyi4ke">
            WHAT WE REMOVE <br data-id="element-527" data-mp="2b4pa9tvsvszf" />
            <span className="text-[#C8102E]" data-id="element-528" data-mp="2b58hafjwwwyo">& HANDLE — EVERYTHING.</span>
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-gray-400 max-w-2xl" data-id="element-529" data-mp="37v7yeye4p3sv">
            Three categories. One crew. Every piece of the job — from the heavy
            specialty items to the last scrap of debris.
          </motion.p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-10 border-b border-white/10 pb-1" data-id="element-530" data-mp="18cd0u4o1t032">
          {serviceCategories.map(cat => {
          const isActive = cat.id === activeTab;
          return <button key={cat.id} onClick={() => setActiveTab(cat.id)} className={`relative flex items-center gap-3 px-4 sm:px-6 py-4 rounded-t-lg transition-colors ${isActive ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`} data-id="element-531" data-mp="1i9hhxq31e5d0">
                <span className={`text-xs font-mono ${isActive ? 'text-[#C8102E]' : 'text-gray-600'}`} data-id="element-532" data-mp="1w0w7ygvd36tk">
                  {cat.number}
                </span>
                <span className="hidden sm:inline" data-id="element-533" data-mp="26050r6ehqfec">{cat.icon}</span>
                <span className="font-bold text-sm sm:text-base uppercase tracking-wide whitespace-nowrap" data-id="element-534" data-mp="25x92ti2n7qpw">
                  {cat.title}
                </span>
                {isActive && <motion.div layoutId="active-tab-underline" className="absolute -bottom-[2px] left-0 right-0 h-[3px] bg-[#C8102E]" data-id="element-535" data-mp="bnon3kjd7yzg" />}
              </button>;
        })}
        </div>

        {/* Active Category Content */}
        <AnimatePresence mode="wait" data-id="element-536" data-mp="2naqpwsvitdt2">
          <motion.div key={active.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.35
        }} data-id="element-537" data-mp="2i9kx6v1v1qjj">
            {/* Category tagline */}
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4" data-id="element-538" data-mp="1zhs30xb21za8">
              <div data-id="element-539" data-mp="3ez2cm1qnaqyf">
                <p className="text-2xl md:text-3xl font-bold text-white" data-id="element-540" data-mp="3eyisqfhfpshr">
                  {active.tagline}
                </p>
                <p className="text-sm text-gray-500 mt-1" data-id="element-541" data-mp="3b0urkup5f3hf">
                  {active.items.length} services in this category
                </p>
              </div>
              <a href="tel:8312407401" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#C8102E] hover:text-red-400 transition-colors" data-id="element-542" data-mp="3nip4oxc483zo">
                <Phone size={16} data-id="element-543" data-mp="3dl107fopji4b" />
                Get a Free Quote
                <ArrowUpRight size={16} data-id="element-544" data-mp="54fuq8vab75y" />
              </a>
            </div>

            {/* Service items grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 rounded-2xl overflow-hidden border border-white/10" data-id="element-545" data-mp="a1g0sjs4w9x2">
              {active.items.map((item, idx) => <motion.div key={item.name} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.04
            }} className="group relative bg-[#141414] hover:bg-[#1A1A1A] p-6 transition-colors cursor-default" data-id="element-546" data-mp="atslamqyloe" data-mpi={idx}>
                  {/* Red corner accent on hover */}
                  <div className="absolute top-0 left-0 w-0 h-[2px] bg-[#C8102E] group-hover:w-full transition-all duration-500" data-id="element-547" data-mp="62vtshd952kx" />

                  <div className="flex items-start gap-4" data-id="element-548" data-mp="3niu3o2fxzbx5">
                    <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-[#C8102E]/10 border border-[#C8102E]/20 flex items-center justify-center text-[#C8102E] group-hover:bg-[#C8102E] group-hover:text-white transition-colors" data-id="element-549" data-mp="3sa3rqylue76x">
                      {item.icon}
                    </div>
                    <div className="min-w-0 flex-1" data-id="element-550" data-mp="34krijyqilz4m">
                      <h4 className="font-bold text-white text-base leading-tight mb-1.5" data-id="element-551" data-mp="34m2q89n2zr10">
                        {item.name}
                      </h4>
                      <p className="text-sm text-gray-400 leading-relaxed" data-id="element-552" data-mp="2pxstgaf4rgt2">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Quote Callout */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-16 relative bg-gradient-to-br from-[#C8102E] to-[#8B0A20] p-10 md:p-14 rounded-2xl overflow-hidden" data-id="element-553" data-mp="2uw44upl2pzde">
          {/* Decorative quote mark */}
          <div className="absolute top-4 right-6 text-[200px] leading-none font-bold text-white/10 select-none pointer-events-none font-serif" data-id="element-554" data-mp="3doyauu0pyh5h">
            "
          </div>

          <div className="relative max-w-3xl" data-id="element-555" data-mp="3nmcneijiulsi">
            <p className="text-xl md:text-3xl font-bold text-white leading-tight mb-6" data-id="element-556" data-mp="3nnnsqmsm81lq">
              You name it. Chances are we've moved it, hauled it, demolished it,
              or cleaned it up.
            </p>
            <p className="text-base md:text-lg text-red-100 mb-8" data-id="element-557" data-mp="3gdgt9ng7ybd">
              If you're not sure — just call. We'll figure it out together.
            </p>
            <a href="tel:8312407401" className="inline-flex items-center gap-3 bg-white text-[#C8102E] px-6 py-3 rounded font-bold hover:bg-gray-100 transition-colors shadow-lg" data-id="element-558" data-mp="d9b3zt3dybgb">
              <Phone size={20} data-id="element-559" data-mp="m7ehc247w1x1" />
              <span data-id="element-560" data-mp="r8k8hvi8bh4g">Call (831) 240-7401</span>
              <ArrowUpRight size={18} data-id="element-561" data-mp="r9lu2twvfm0f" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}